import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ArrowLeft, ArrowRight, CalendarIcon, Check, Upload } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const ProfileSetup = () => {
  const { t } = useTranslation();
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(null);
  const [date, setDate] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [languageSearch, setLanguageSearch] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  
  const [formData, setFormData] = useState({
    about_me: "",
    date_of_birth: "",
    height_cm: "",
    marital_status: "",
    has_children: false,
    wants_children: "",
    muslim_type: "",
    religiosity_level: "",
    prays_regularly: false,
    hijab_preference: "",
    beard_preference: "",
    fashion_style: "",
    fashion_style_other: "",
    ethnicity: "",
    country: "",
    city: "",
    education: "",
    profession: "",
    income_range: "",
    languages: [],
    looking_for: "",
    deal_breakers: [],
    interests: []
  });

  const totalSteps = 5;

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/config/options`);
      setOptions(response.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    
    const formDataObj = new FormData();
    formDataObj.append("file", photoFile);
    
    try {
      await axios.post(`${API}/profile/photo`, formDataObj, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
    } catch (error) {
      console.error("Error uploading photo:", error);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Upload photo first if selected
      if (photoFile) {
        await uploadPhoto();
      }
      
      // Update profile
      const profileData = { ...formData };
      if (date) {
        profileData.date_of_birth = format(date, "yyyy-MM-dd");
      }
      if (profileData.height_cm) {
        profileData.height_cm = parseInt(profileData.height_cm);
      }
      
      await axios.put(`${API}/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await refreshUser();
      toast.success("Profile completed successfully!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.errorSavingProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const toggleArrayField = (field, value) => {
    const current = formData[field] || [];
    if (current.includes(value)) {
      updateField(field, current.filter(v => v !== value));
    } else {
      updateField(field, [...current, value]);
    }
  };

  if (!options) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-emerald-900 font-serif text-2xl">Loading...</div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6" data-testid="step-1-content">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl text-stone-900 mb-2">Let's Start with the Basics</h2>
              <p className="text-stone-600">Tell us a bit about yourself</p>
            </div>

            {/* Photo Upload */}
            <div className="flex flex-col items-center mb-8">
              <div 
                className="w-32 h-32 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-emerald-500 transition-colors"
                onClick={() => document.getElementById('photo-input').click()}
                data-testid="photo-upload-area"
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-8 h-8 text-stone-400" />
                )}
              </div>
              <input
                id="photo-input"
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="photo-input"
              />
              <p className="text-sm text-stone-500 mt-2">Upload your photo</p>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">About Me</Label>
              <Textarea
                value={formData.about_me}
                onChange={(e) => updateField("about_me", e.target.value)}
                placeholder="Tell potential matches about yourself..."
                className="min-h-[120px] bg-stone-50 border-stone-200 focus:border-emerald-500"
                data-testid="about-me-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Date of Birth</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-stone-50 border-stone-200 h-12"
                    data-testid="dob-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : t('profileSetup.selectBirthDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d > new Date() || d < new Date("1940-01-01")}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={1940}
                    toYear={2006}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-700">Height (cm)</Label>
                <Input
                  type="number"
                  value={formData.height_cm}
                  onChange={(e) => updateField("height_cm", e.target.value)}
                  placeholder="e.g., 170"
                  className="bg-stone-50 border-stone-200 h-12"
                  data-testid="height-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Marital Status</Label>
                <Select value={formData.marital_status} onValueChange={(v) => updateField("marital_status", v)}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="marital-status-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never_married">Never Married</SelectItem>
                    <SelectItem value="divorced">Divorced</SelectItem>
                    <SelectItem value="widowed">Widowed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6" data-testid="step-2-content">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl text-stone-900 mb-2">Your Islamic Background</h2>
              <p className="text-stone-600">Help us find someone compatible with your faith</p>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Muslim Type</Label>
              <Select value={formData.muslim_type} onValueChange={(v) => updateField("muslim_type", v)}>
                <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="muslim-type-select">
                  <SelectValue placeholder="Select your denomination" />
                </SelectTrigger>
                <SelectContent>
                  {options.muslim_types.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Religiosity Level</Label>
              <RadioGroup
                value={formData.religiosity_level}
                onValueChange={(v) => updateField("religiosity_level", v)}
                className="flex flex-wrap gap-4"
                data-testid="religiosity-radio"
              >
                {["Very Religious", "Moderately Religious", "Somewhat Religious", "Not Very Religious"].map((level) => (
                  <div key={level} className="flex items-center space-x-2">
                    <RadioGroupItem value={level} id={level} className="border-emerald-900 text-emerald-900" />
                    <Label htmlFor={level} className="text-stone-700 cursor-pointer">{level}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                id="prays"
                checked={formData.prays_regularly}
                onCheckedChange={(checked) => updateField("prays_regularly", checked)}
                className="border-emerald-900 data-[state=checked]:bg-emerald-900"
                data-testid="prays-checkbox"
              />
              <Label htmlFor="prays" className="text-stone-700 cursor-pointer">I pray regularly (5 daily prayers)</Label>
            </div>

            {user?.gender === "Female" && (
              <div className="space-y-2">
                <Label className="text-stone-700">Do you wear Hijab?</Label>
                <Select value={formData.hijab_preference} onValueChange={(v) => updateField("hijab_preference", v)}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="hijab-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Always wears hijab">Yes, always</SelectItem>
                    <SelectItem value="Sometimes wears hijab">Yes, sometimes</SelectItem>
                    <SelectItem value="Does not wear hijab">No</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {user?.gender === "Male" && (
              <div className="space-y-2">
                <Label className="text-stone-700">Hijab Preference for Partner</Label>
                <Select value={formData.hijab_preference} onValueChange={(v) => updateField("hijab_preference", v)}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="hijab-pref-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="must_wear">Must wear hijab</SelectItem>
                    <SelectItem value="prefer_hijab">Prefer hijab</SelectItem>
                    <SelectItem value="no_preference">No preference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Beard preference for males */}
            {user?.gender === "Male" && (
              <div className="space-y-2">
                <Label className="text-stone-700">Beard Style</Label>
                <Select value={formData.beard_preference} onValueChange={(v) => updateField("beard_preference", v)}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="beard-select">
                    <SelectValue placeholder="Select your beard style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full beard">Full beard</SelectItem>
                    <SelectItem value="Stubble">Stubble</SelectItem>
                    <SelectItem value="Clean shaven">Clean shaven</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Beard preference for partner (females) */}
            {user?.gender === "Female" && (
              <div className="space-y-2">
                <Label className="text-stone-700">Partner's Beard Preference</Label>
                <Select value={formData.beard_preference} onValueChange={(v) => updateField("beard_preference", v)}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="beard-pref-select">
                    <SelectValue placeholder="Select preference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full beard">Prefer full beard</SelectItem>
                    <SelectItem value="Stubble">Prefer stubble</SelectItem>
                    <SelectItem value="Clean shaven">Prefer clean shaven</SelectItem>
                    <SelectItem value="Prefer not to say">No preference</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Fashion/Clothing Style - for everyone */}
            <div className="space-y-2">
              <Label className="text-stone-700">Clothing/Fashion Style</Label>
              <Select value={formData.fashion_style} onValueChange={(v) => updateField("fashion_style", v)}>
                <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="fashion-select">
                  <SelectValue placeholder="Select your style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Modest/Traditional">Modest/Traditional (loose, covering)</SelectItem>
                  <SelectItem value="Smart Casual">Smart Casual</SelectItem>
                  <SelectItem value="Modern/High Fashion">Modern/High Fashion</SelectItem>
                  <SelectItem value="Mix of styles">Mix of styles</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Custom fashion style input */}
            {formData.fashion_style === "Other" && (
              <div className="space-y-2">
                <Label className="text-stone-700">Describe your style</Label>
                <Input
                  value={formData.fashion_style_other}
                  onChange={(e) => updateField("fashion_style_other", e.target.value)}
                  placeholder="Describe your clothing/fashion style..."
                  className="bg-stone-50 border-stone-200 h-12"
                  data-testid="fashion-other-input"
                />
              </div>
            )}
          </div>
        );

      case 3:
        const allLanguages = [
          "Afrikaans", "Albanian", "Amharic", "Arabic", "Armenian", "Assamese", "Azerbaijani",
          "Balochi", "Bambara", "Bashkir", "Basque", "Belarusian", "Bengali", "Bhojpuri", "Bosnian", "Bulgarian", "Burmese",
          "Cantonese", "Catalan", "Cebuano", "Chechen", "Chinese (Mandarin)", "Chinese (Min)", "Chinese (Wu)", "Croatian", "Czech",
          "Danish", "Dari", "Divehi", "Dutch",
          "English", "Estonian",
          "Fijian", "Filipino", "Finnish", "French", "Fula",
          "Galician", "Georgian", "German", "Greek", "Guarani", "Gujarati",
          "Haitian Creole", "Hausa", "Hawaiian", "Hebrew", "Hindi", "Hmong", "Hungarian",
          "Icelandic", "Igbo", "Indonesian", "Irish", "Italian",
          "Japanese", "Javanese",
          "Kannada", "Kashmiri", "Kazakh", "Khmer", "Kinyarwanda", "Kirundi", "Korean", "Kurdish", "Kyrgyz",
          "Lao", "Latin", "Latvian", "Lingala", "Lithuanian", "Luganda", "Luxembourgish",
          "Macedonian", "Malagasy", "Malay", "Malayalam", "Maltese", "Maori", "Marathi", "Mongolian",
          "Navajo", "Nepali", "Norwegian",
          "Odia", "Oromo",
          "Pashto", "Persian (Farsi)", "Polish", "Portuguese", "Punjabi",
          "Quechua",
          "Romanian", "Russian", "Rwandan",
          "Samoan", "Sanskrit", "Serbian", "Shona", "Sindhi", "Sinhala", "Slovak", "Slovenian", "Somali", "Spanish", "Sundanese", "Swahili", "Swedish",
          "Tagalog", "Tajik", "Tamil", "Tatar", "Telugu", "Thai", "Tibetan", "Tigrinya", "Turkish", "Turkmen",
          "Ukrainian", "Urdu", "Uyghur", "Uzbek",
          "Vietnamese",
          "Welsh", "Wolof",
          "Xhosa",
          "Yiddish", "Yoruba",
          "Zulu"
        ];
        
        const allCountries = [
          "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Azerbaijan",
          "Bahrain", "Bangladesh", "Belgium", "Bosnia and Herzegovina", "Brazil", "Brunei", "Bulgaria",
          "Canada", "China", "Colombia", "Croatia", "Cyprus", "Czech Republic",
          "Denmark", "Djibouti",
          "Egypt", "Ethiopia",
          "Finland", "France",
          "Gambia", "Germany", "Ghana", "Greece",
          "Hungary",
          "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
          "Japan", "Jordan",
          "Kazakhstan", "Kenya", "Kosovo", "Kuwait", "Kyrgyzstan",
          "Lebanon", "Libya",
          "Malaysia", "Maldives", "Mali", "Malta", "Mauritania", "Mauritius", "Mexico", "Morocco", "Myanmar",
          "Netherlands", "New Zealand", "Niger", "Nigeria", "North Macedonia", "Norway",
          "Oman",
          "Pakistan", "Palestine", "Philippines", "Poland", "Portugal",
          "Qatar",
          "Romania", "Russia",
          "Saudi Arabia", "Senegal", "Serbia", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Somalia", "South Africa", "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria",
          "Tajikistan", "Tanzania", "Thailand", "Tunisia", "Turkey", "Turkmenistan",
          "UAE", "Uganda", "Ukraine", "United Kingdom", "United States", "Uzbekistan",
          "Yemen",
          "Other"
        ];
        
        const filteredLanguages = languageSearch 
          ? allLanguages.filter(lang => lang.toLowerCase().includes(languageSearch.toLowerCase()))
          : allLanguages;
          
        const filteredCountries = countrySearch 
          ? allCountries.filter(country => country.toLowerCase().includes(countrySearch.toLowerCase()))
          : allCountries;
        
        return (
          <div className="space-y-6" data-testid="step-3-content">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl text-stone-900 mb-2">Where Are You From?</h2>
              <p className="text-stone-600">Location and background details</p>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Ethnicity</Label>
              <Select value={formData.ethnicity} onValueChange={(v) => updateField("ethnicity", v)}>
                <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="ethnicity-select">
                  <SelectValue placeholder="Select your ethnicity" />
                </SelectTrigger>
                <SelectContent>
                  {options.ethnicities.map((eth) => (
                    <SelectItem key={eth} value={eth}>{eth}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Country</Label>
              <Input
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder="Search countries..."
                className="bg-stone-50 border-stone-200 h-10 mb-2"
                data-testid="country-search"
              />
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border border-stone-200 rounded-xl">
                {filteredCountries.map((country) => (
                  <button
                    key={country}
                    type="button"
                    onClick={() => {
                      updateField("country", country);
                      setCountrySearch("");
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      formData.country === country
                        ? "bg-emerald-900 text-white"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                    data-testid={`country-${country.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {country}
                  </button>
                ))}
              </div>
              {formData.country && (
                <p className="text-emerald-700 text-sm mt-1">Selected: {formData.country}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">City</Label>
              <Input
                value={formData.city}
                onChange={(e) => updateField("city", e.target.value)}
                placeholder="Enter your city"
                className="bg-stone-50 border-stone-200 h-12"
                data-testid="city-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Languages Spoken</Label>
              <Input
                value={languageSearch}
                onChange={(e) => setLanguageSearch(e.target.value)}
                placeholder="Search languages..."
                className="bg-stone-50 border-stone-200 h-10 mb-2"
                data-testid="language-search"
              />
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border border-stone-200 rounded-xl">
                {filteredLanguages.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleArrayField("languages", lang)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      formData.languages?.includes(lang)
                        ? "bg-emerald-900 text-white"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                    data-testid={`lang-${lang.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              {formData.languages?.length > 0 && (
                <p className="text-emerald-700 text-sm mt-1">Selected: {formData.languages.join(", ")}</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6" data-testid="step-4-content">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl text-stone-900 mb-2">Education & Career</h2>
              <p className="text-stone-600">Your professional background</p>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Education Level</Label>
              <Select value={formData.education} onValueChange={(v) => updateField("education", v)}>
                <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="education-select">
                  <SelectValue placeholder="Select your education" />
                </SelectTrigger>
                <SelectContent>
                  {options.education_levels.map((edu) => (
                    <SelectItem key={edu} value={edu}>{edu}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Profession</Label>
              <Input
                value={formData.profession}
                onChange={(e) => updateField("profession", e.target.value)}
                placeholder="e.g., Software Engineer, Doctor, Teacher"
                className="bg-stone-50 border-stone-200 h-12"
                data-testid="profession-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Annual Income Range</Label>
              <Select value={formData.income_range} onValueChange={(v) => updateField("income_range", v)}>
                <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="income-select">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                  <SelectItem value="under_20k">Under £20,000</SelectItem>
                  <SelectItem value="20k_40k">£20,000 - £40,000</SelectItem>
                  <SelectItem value="40k_60k">£40,000 - £60,000</SelectItem>
                  <SelectItem value="60k_80k">£60,000 - £80,000</SelectItem>
                  <SelectItem value="80k_100k">£80,000 - £100,000</SelectItem>
                  <SelectItem value="over_100k">Over £100,000</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-700">Do you have children?</Label>
                <Select value={formData.has_children ? "yes" : "no"} onValueChange={(v) => updateField("has_children", v === "yes")}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="has-children-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-stone-700">Want children?</Label>
                <Select value={formData.wants_children} onValueChange={(v) => updateField("wants_children", v)}>
                  <SelectTrigger className="bg-stone-50 border-stone-200 h-12" data-testid="wants-children-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="maybe">Maybe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6" data-testid="step-5-content">
            <div className="text-center mb-8">
              <h2 className="font-serif text-2xl text-stone-900 mb-2">What Are You Looking For?</h2>
              <p className="text-stone-600">Help us understand your preferences</p>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">What are you looking for in a partner?</Label>
              <Textarea
                value={formData.looking_for}
                onChange={(e) => updateField("looking_for", e.target.value)}
                placeholder="Describe your ideal partner..."
                className="min-h-[100px] bg-stone-50 border-stone-200 focus:border-emerald-500"
                data-testid="looking-for-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Deal Breakers (Select all that apply)</Label>
              <div className="flex flex-wrap gap-2">
                {["Smoking", "Not praying", "No hijab", "Different sect", "Living with parents", "Long distance", "Has children", "Previously married"].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleArrayField("deal_breakers", item)}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      formData.deal_breakers?.includes(item)
                        ? "bg-red-100 text-red-700 border border-red-300"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                    data-testid={`dealbreaker-${item.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700">Your Interests & Hobbies</Label>
              <div className="flex flex-wrap gap-2">
                {["Reading", "Sports", "Travel", "Cooking", "Technology", "Art", "Music", "Fitness", "Nature", "Photography", "Gaming", "Volunteering"].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleArrayField("interests", item)}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      formData.interests?.includes(item)
                        ? "bg-emerald-900 text-white"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                    data-testid={`interest-${item.toLowerCase()}`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} alt="SingleSeen" className="h-20 w-auto" />
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-rose-600">Step {step} of {totalSteps}</span>
            <span className="text-sm text-rose-600">{Math.round((step / totalSteps) * 100)}% Complete</span>
          </div>
          <div className="h-2 bg-rose-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mb-8">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  s < step ? "bg-emerald-500 text-white" :
                  s === step ? "bg-emerald-900 text-white" :
                  "bg-stone-200 text-stone-500"
                }`}
              >
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1}
            className="border-stone-300 text-stone-700 rounded-full px-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            disabled={loading}
            className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full px-6"
            data-testid="next-btn"
          >
            {loading ? "Saving..." : step === totalSteps ? "Complete Profile" : "Continue"}
            {!loading && step < totalSteps && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
